import { create } from 'zustand';
import { apiClient } from '../lib/api';

/**
 * People hub landing page. The API decides the view from the caller's roles
 * (self / team / org) and only returns the sections that view may see.
 * Dates are plain 'YYYY-MM-DD' strings; timestamps are ISO strings.
 */
export type DashboardView = 'org' | 'team' | 'self';
export type DayStatus = 'checked_in' | 'checked_out' | 'on_leave' | 'not_checked_in';

export interface ApprovalQueue<T> { total: number; items: T[] }
export interface PendingLeave {
  id: string; employee: string; type: string; startDate: string; endDate: string; days: number; requestedAt: string;
}
export interface PendingRegularization {
  id: string; employee: string; date: string; reason: string | null; requestedAt: string;
}

export interface MyDashboard {
  employee: { id: string; name: string; code: string | null; designation: string | null; department: string | null; joinDate: string | null };
  attendance: { status: DayStatus; checkinAt: string | null; openSince: string | null; lastCheckout: string | null; sessions: number; hours: number };
  week: { date: string; hours: number; status: string | null }[];
  leaveBalances: { name: string; code: string; allocated: number; taken: number; remaining: number }[];
  leaveRequests: { id: string; type: string; startDate: string; endDate: string; days: number; status: string }[];
  pendingRegularizations: number;
  nextShift: { day: string; name: string; start: string | null; end: string | null; hours: number } | null;
  latestPayslip: {
    periodStart: string; periodEnd: string; gross: number; net: number; status: string; paymentDate: string | null; currency: string;
  } | null;
}

export interface TeamMember {
  id: string; name: string; designation: string | null; status: DayStatus; checkinAt: string | null; hours: number;
}
export interface TeamDashboard {
  size: number; present: number; onLeave: number; notCheckedIn: number;
  members: TeamMember[];
  pendingLeave: ApprovalQueue<PendingLeave>;
  pendingRegularizations: ApprovalQueue<PendingRegularization>;
}

export interface OrgDashboard {
  headcount: { total: number; active: number; joined30d: number; exited30d: number };
  attendanceToday: { present: number; ongoing: number; onLeave: number; notCheckedIn: number; rate: number };
  attendanceTrend: { date: string; present: number; remote: number }[];
  byDepartment: { department: string; count: number }[];
  byEmploymentType: { type: string; count: number }[];
  pendingLeave: ApprovalQueue<PendingLeave>;
  pendingRegularizations: ApprovalQueue<PendingRegularization>;
  recruitment: {
    openJobs: number; openings: number;
    candidatesByStatus: { status: string; count: number }[];
    upcomingInterviews: { id: string; candidate: string; role: string | null; type: string | null; at: string; minutes: number }[];
  };
  lifecycle: { onboarding: number; offboarding: number; documentsExpiring: number };
  recentJoiners: { id: string; name: string; designation: string | null; department: string | null; joinDate: string }[];
  anniversaries: { id: string; name: string; years: number; date: string }[];
}

export interface PeopleDashboardData {
  view: DashboardView;
  roles: string[];
  today: string;
  me: MyDashboard | null;
  holidays: { name: string; date: string; type: string | null }[];
  team: TeamDashboard | null;
  org: OrgDashboard | null;
}

interface PeopleDashboardState {
  data: PeopleDashboardData | null;
  isLoading: boolean;
  error: string | null;
  lastLoadedAt: number | null;
  fetchDashboard: (opts?: { silent?: boolean }) => Promise<void>;
}

export const usePeopleDashboardStore = create<PeopleDashboardState>((set) => ({
  data: null,
  isLoading: false,
  error: null,
  lastLoadedAt: null,

  fetchDashboard: async (opts) => {
    // Silent refreshes (after check-in, on an interval) keep the current page on screen.
    if (!opts?.silent) set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/dashboard');
      set({
        data: response.data?.data ?? response.data,
        isLoading: false,
        error: null,
        lastLoadedAt: Date.now(),
      });
    } catch (error: any) {
      set({
        isLoading: false,
        error: error?.response?.data?.message || error?.message || 'Could not load the People dashboard',
      });
    }
  },
}));
